import { computed, Ref, ref } from '@vue/composition-api';
import { getCurrentUserGroups, getGroup } from '@front-app/services/GroupsApi/GroupsApi';
import {
	Groups,
	GroupServiceLevel,
	GroupWithCommunity,
} from '@front-app/services/GroupsApi/GroupModel';
import { UserRole } from '@front-app/services/UserApi/UserModel';
import { STATUS } from '@front-app/constants/state';
import { getGlobalContext } from '@front-app/util/env';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { VisibilityOption } from '@front-app/services/GroupsApi/GroupEvent';
import { STARTABLE_STATUSES, useCache } from '../util/cache';

export interface GroupsStore {
	fetchGroupsOnce(): Promise<void>;
	fetchGroups(): Promise<void>;
	fetchGroup(id: string, force?: boolean, silent?: boolean): Promise<void>;
	groupById(id: string): GroupWithCommunity;
	isGroupPresent(id: string): boolean;
	isGroupLoaded(id: string): boolean;
	isGroupLoading(id: string): boolean;
	isGroupErrorred(id: string): boolean;
	updateLocalGroup(id: string, updates: Partial<GroupWithCommunity>): void;
	reset(): void;
	groups: Ref<Groups>;
}

/* Prevent refetching groups until 5 minutes has passed */
const TIMEOUT = 1000 * 60 * 5;

export const getGroupsStore = (): GroupsStore => {
	let pend: Promise<void> = Promise.resolve();
	let tid: undefined | number;
	let hasFetched = false;
	const groupCache = useCache({}, getEmptyGroup);
	const usersOwnGroupIds = ref([]) as Ref<string[]>;

	const groupById = (id: string) => groupCache.get(id);

	const isGroupPresent = (id: string) => groupCache.has(id);

	const isGroupLoaded = (id: string) => groupCache.getStatus(id).value === STATUS.SUCCESS;

	const isGroupLoading = (id: string) => {
		const status = groupCache.getStatus(id).value;
		return status === STATUS.LOADING || status === STATUS.UNSTARTED;
	};

	const isGroupErrorred = (id: string) => groupCache.getStatus(id).value === STATUS.ERROR;

	const groups = computed(() => usersOwnGroupIds.value.map(groupById));

	const fetchGroups = () => {
		pend = Promise.resolve();
		getGlobalContext().clearTimeout(tid);
		tid = getGlobalContext().setTimeout(() => {
			hasFetched = false;
			pend = Promise.resolve();
		}, TIMEOUT);
		hasFetched = true;
		pend = getCurrentUserGroups()
			.then((userGroups) => {
				userGroups.forEach((group) => {
					/**
					 * This endpoint returns neither numMembers nor community, so we should re-use
					 * the existing values if present
					 */
					// eslint-disable-next-line no-param-reassign
					group.numMembers = groupCache.get(group.id).numMembers;
					// eslint-disable-next-line no-param-reassign
					group.community = groupCache.get(group.id).community;
					groupCache.set(group.id, group);
				});
				usersOwnGroupIds.value = userGroups.map(({ id }) => id);
			})
			.catch((e) => {
				// eslint-disable-next-line no-console
				console.error('Error fetching or parsing groups', e);
				hasFetched = false;
			});
		return pend;
	};

	const fetchGroupsOnce = () => {
		if (hasFetched) {
			return pend;
		}
		return fetchGroups();
	};

	const fetchGroup = async (id: string, force = false, silent = false) => {
		if (force || STARTABLE_STATUSES.includes(groupCache.getStatus(id).value)) {
			const promisedResult = getGroup(id).then((result) => {
				if (result) {
					groupCache.set(id, result);
				}
			});
			if (silent) {
				await promisedResult;
			} else {
				const { track } = useAsyncStatus(groupCache.getStatus(id));
				await track(promisedResult);
			}
		}
	};

	return {
		fetchGroupsOnce,
		fetchGroups,
		fetchGroup,
		groupById,
		isGroupPresent,
		isGroupLoaded,
		isGroupLoading,
		isGroupErrorred,
		updateLocalGroup: groupCache.update,
		reset: groupCache.clear,
		groups,
	};
};

function getEmptyGroup(id: string): GroupWithCommunity {
	return {
		// Hashed ID
		id,
		name: '',
		description: '',
		// Group type header, displays resource bundle key
		type: GroupServiceLevel.STANDARD,
		// User role in relation to group
		role: UserRole.DEPARTEE,
		// logo image source
		logoSrc: '',
		logo: {
			src: null,
			params: null,
		},
		// banner image source
		bannerSrc: '',
		banner: {
			src: null,
			params: null,
		},
		// Base64 encoded link associated with the banner image
		bannerLink: '',
		// HTML storage, Base64 encoded
		widgetHeaderHtml: '',
		// HTML storage, Base64 encoded
		widgetHtml: '',
		// HTML storage, Base64 encoded. Does not indicate if terms are required
		termsAndConditionsHtml: '',
		// Indicates if members need to accept or renew the terms of use
		termsRequired: false,
		// Indicates if members need to make a payment
		paymentRequired: false,
		created: new Date(),
		categories: [],
		/**
		 * GUI display tokens. Includes tokens for TOU and paywall (userTermsRequired
		 * and userPayRequired). Present if a user has not accepted the required terms
		 * of use or made required payment (see termsRequired and paymentRequired)
		 */
		preferenceTokens: [],
		version: 0,
		lastVisited: 0,
		pageHtml: '',
		pageName: '',
		numMembers: 0,
		conditions: {
			maxMembers: 0,
			maxModerators: 0,
			maxAdministrators: 0,
			maxUploadFileSize: 0,
			maxTotalUploadFileSize: 0,
		},
		// Number of used bytes in file upload quota
		totalFileBytes: 0,
		accessSettings: null,
		adminSettings: {
			notifyAllMembersDiscussionEnabled: false,
			notifyAllMembersDiscussionDefault: false,
			notifyAllMembersFileEnabled: false,
			notifyAllMembersFileDefault: false,
			notifyModeratorsAllDiscussions: false,
			notifyModeratorsNewFiles: false,
		},
		visibilitySettings: {
			visibility: VisibilityOption.MEMBERS_ONLY,
		},
		community: null,
	};
}
